import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { guideProfileApi } from '@/features/guide-profile/api/guide-profile.api';

/** Survives the `?lang=` switch and reloads within the tab. */
const REFERRAL_STORAGE_KEY = 'trplin_ref';

const GLASS_BORDER = 'rgba(255, 255, 255, 0.28)';
const GLASS_PLACEHOLDER = 'rgba(255, 255, 255, 0.6)';

function readStoredRef(): string | null {
  try {
    return sessionStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeRef(code: string | null) {
  try {
    if (code) sessionStorage.setItem(REFERRAL_STORAGE_KEY, code);
    else sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // Storage blocked: the code still works for this page view.
  }
}

/**
 * The referral code from an invite link (`?ref=`), kept for the whole tab
 * session so it isn't lost between steps or language switches.
 */
export function useLinkedReferralCode() {
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('ref')?.trim().toUpperCase() || null;
  const [code, setCode] = useState<string | null>(() => fromUrl ?? readStoredRef());

  useEffect(() => {
    if (fromUrl) {
      storeRef(fromUrl);
      setCode(fromUrl);
    }
  }, [fromUrl]);

  return {
    code,
    /** Drops the code from storage and the URL, so a reload doesn't bring it back. */
    clear: () => {
      storeRef(null);
      setCode(null);
      if (searchParams.has('ref')) {
        const next = new URLSearchParams(searchParams);
        next.delete('ref');
        setSearchParams(next, { replace: true });
      }
    },
  };
}

export interface ReferralValue {
  code: string;
  source: 'LINK' | 'MANUAL';
}

interface ReferralCodeFieldProps {
  /** Reports the code to submit, or null when there is nothing usable. */
  onChange: (value: ReferralValue | null) => void;
  /** Field error returned by the server (e.g. self-referral). */
  serverError?: string;
}

/**
 * Link mode: the code is locked in, read-only, with a "Referred by" chip.
 * Direct mode: an optional, collapsed field to type a Guide ID by hand.
 * The server re-validates either way — the lock here is UX, not security.
 */
export function ReferralCodeField({ onChange, serverError }: ReferralCodeFieldProps) {
  const { t } = useTranslation('guideProfile');
  const linked = useLinkedReferralCode();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [checkedCode, setCheckedCode] = useState('');

  const lookupCode = linked.code ?? checkedCode;
  const check = useQuery({
    queryKey: ['referral-code-check', lookupCode],
    queryFn: () => guideProfileApi.checkReferralCode(lookupCode),
    enabled: lookupCode.length >= 5,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  const linkInvalid = Boolean(linked.code && check.data && !check.data.valid);

  useEffect(() => {
    if (linked.code) {
      onChange(linkInvalid ? null : { code: linked.code, source: 'LINK' });
    } else {
      onChange(typed.trim() ? { code: typed.trim().toUpperCase(), source: 'MANUAL' } : null);
    }
    // onChange is a setter from the parent; re-running on its identity is noise.
  }, [linked.code, linkInvalid, typed]);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: 'rgba(255, 255, 255, 0.08)',
      borderRadius: '8px',
      color: 'common.white',
      '& fieldset': { borderColor: GLASS_BORDER },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
      '&.Mui-focused fieldset': { borderColor: 'common.white', borderWidth: '1px' },
      '&.Mui-error fieldset': { borderColor: 'error.light' },
      '&.Mui-disabled': { bgcolor: 'rgba(255, 255, 255, 0.12)' },
    },
    '& input::placeholder': { color: GLASS_PLACEHOLDER, opacity: 1 },
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: '#FFFFFF',
      fontWeight: 700,
      letterSpacing: '0.04em',
    },
    '& .MuiFormHelperText-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      '&.Mui-error': { color: 'error.light' },
    },
  };

  if (linked.code) {
    const helper = serverError
      ? serverError
      : linkInvalid
        ? t('applicationForm.referral.invalidLink')
        : check.data?.valid && check.data.referrerFirstName
          ? t('applicationForm.referral.validHint', { name: check.data.referrerFirstName })
          : t('applicationForm.referral.lockedHint');

    return (
      <Box>
        <Chip
          icon={<CardGiftcardOutlinedIcon />}
          label={t('applicationForm.referral.lockedLabel')}
          size="small"
          sx={{
            mb: 1,
            bgcolor: 'rgba(200, 169, 110, 0.22)',
            color: '#F5E6C4',
            fontWeight: 700,
            '& .MuiChip-icon': { color: '#F5E6C4' },
          }}
        />
        <TextField
          value={linked.code}
          disabled
          fullWidth
          sx={fieldSx}
          error={Boolean(serverError) || linkInvalid}
          helperText={helper}
          inputProps={{ 'aria-label': t('applicationForm.referral.lockedLabel'), readOnly: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ color: GLASS_PLACEHOLDER, mr: 1 }}>
                <LockOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: check.isFetching ? (
              <CircularProgress size={16} sx={{ color: GLASS_PLACEHOLDER }} />
            ) : check.data?.valid ? (
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.light' }} />
            ) : null,
          }}
        />
        {/* The server can still refuse a valid link (existing account, own ID): let them drop it. */}
        {(serverError || linkInvalid) && (
          <Typography
            component="button"
            type="button"
            onClick={linked.clear}
            sx={{
              mt: 0.5,
              p: 0,
              border: 0,
              bgcolor: 'transparent',
              color: GLASS_PLACEHOLDER,
              fontSize: '0.75rem',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            {t('applicationForm.referral.remove')}
          </Typography>
        )}
      </Box>
    );
  }

  const typedError = checkedCode && check.data && !check.data.valid ? check.data.message : undefined;

  return (
    <Box>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          p: 0,
          border: 0,
          bgcolor: 'transparent',
          color: 'primary.light',
          font: 'inherit',
          fontSize: '0.875rem',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <CardGiftcardOutlinedIcon fontSize="small" />
        {t('applicationForm.referral.toggle')}
      </Box>
      <Collapse in={open || Boolean(typed)}>
        <TextField
          value={typed}
          onChange={(event) => setTyped(event.target.value.toUpperCase())}
          onBlur={() => setCheckedCode(typed.trim())}
          placeholder={t('applicationForm.referral.placeholder')}
          fullWidth
          sx={{ ...fieldSx, mt: 1.25 }}
          error={Boolean(serverError || typedError)}
          helperText={
            serverError ??
            typedError ??
            (check.isFetching
              ? t('applicationForm.referral.checking')
              : checkedCode && check.data?.valid && check.data.referrerFirstName
                ? t('applicationForm.referral.validHint', { name: check.data.referrerFirstName })
                : undefined)
          }
          inputProps={{ 'aria-label': t('applicationForm.referral.label'), maxLength: 20 }}
          InputProps={{
            endAdornment:
              checkedCode && check.data?.valid ? (
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.light' }} />
              ) : null,
          }}
        />
        {typed && (
          <Typography
            component="button"
            type="button"
            onClick={() => {
              setTyped('');
              setCheckedCode('');
            }}
            sx={{
              mt: 0.5,
              p: 0,
              border: 0,
              bgcolor: 'transparent',
              color: GLASS_PLACEHOLDER,
              fontSize: '0.75rem',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            {t('applicationForm.referral.remove')}
          </Typography>
        )}
      </Collapse>
    </Box>
  );
}
